import React, { useState, useRef, useEffect } from 'react';
import { auth, db, storage, functions } from './firebase';
import { doc, getDoc, updateDoc, deleteDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { signOut } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import Nav from './Nav';
import Toast from './Toast';
import downscaleImage from './downscaleImage';
import { loadOwnSocials, saveSocials, loadOwnPhone, savePhone } from './socials';
import { MONTHS, daysInMonth, toMMDD, parseMMDD } from './birthday';
import { ACCENTS, ACCENT_LEVELS, DEFAULT_ACCENT_LEVEL } from './accents';
import { SOCIAL_PLATFORMS, SUPPORTED_PLATFORM_NAMES, detectPlatform, buildSocialUrl, platformLabel as getPlatformLabel } from './platforms';
import AvatarCropper from './AvatarCropper';

// Per-link visibility tiers, most private first. New links default to private.
const VIS = [
  { key: 'private', label: 'Private', desc: 'Only you can see this' },
  { key: 'top8', label: 'Top 8', desc: 'Only people in your Top 8 can see this' },
  { key: 'connections', label: 'Connections', desc: 'Only people you are connected with can see this' },
  { key: 'public', label: 'Public', desc: 'Anyone can see this' },
];

// A phone number is more sensitive: personal accounts can only keep it to their
// Top 8 or private. Business accounts can also expose it to connections / public.
const PHONE_VIS_PERSONAL = ['private', 'top8'];
const PHONE_VIS_BUSINESS = ['private', 'top8', 'connections', 'public'];

function Settings({ currentUser, currentUserProfile, onProfileUpdate, onSignOut }) {
  const navigate = useNavigate();
  const [displayName, setDisplayName] = useState(currentUserProfile?.displayName || '');
  const [bio, setBio] = useState(currentUserProfile?.bio || '');
  const [photoPreview, setPhotoPreview] = useState(currentUserProfile?.photoURL || null);
  const [photoFile, setPhotoFile] = useState(null);
  const [accent, setAccent] = useState(currentUserProfile?.profileAccent || 'purple');
  const [accentLevel, setAccentLevel] = useState(currentUserProfile?.accentLevel || DEFAULT_ACCENT_LEVEL);
  const [saving, setSaving] = useState(false);
  const [copiedUrl, setCopiedUrl] = useState(false);
  const [toast, setToast] = useState(null);
  const fileInputRef = useRef();

  // Socials (normalize: every social has a visibility tier, default public)
  const [socials, setSocials] = useState(
    (currentUserProfile?.socials || []).map(s => ({ url: s.url, visibility: s.visibility || 'public' }))
  );
  const [newSocialUrl, setNewSocialUrl] = useState('');
  const [newSocialPlatform, setNewSocialPlatform] = useState('');
  const [socialError, setSocialError] = useState('');
  const [showSupported, setShowSupported] = useState(false);

  // Optional birthday (month + day only). Drives the birthday tile frame.
  const initBday = parseMMDD(currentUserProfile?.birthday);
  const [bMonth, setBMonth] = useState(initBday?.month || '');
  const [bDay, setBDay] = useState(initBday?.day || '');

  // Account type. Entity accounts pick a category and can request verification.
  const [accountType, setAccountType] = useState(currentUserProfile?.accountType || 'personal');
  const isBusiness = accountType === 'business';
  // Verification is admin-granted; only VERIFIED entities can post a public phone
  // or show the badge.
  const businessVerified = currentUserProfile?.businessVerified === true;
  const [entityCategory, setEntityCategory] = useState(currentUserProfile?.entityCategory || 'business');
  const [verifyWebsite, setVerifyWebsite] = useState('');
  const [verifyNotes, setVerifyNotes] = useState('');
  const [verifyBusy, setVerifyBusy] = useState(false);
  const [verifyStatus, setVerifyStatus] = useState(currentUserProfile?.verificationStatus || null);

  // Phone number + its visibility (loaded from the secured store).
  const [phone, setPhone] = useState('');
  const [phoneVisibility, setPhoneVisibility] = useState('private');

  // Unsaved-changes tracking for the floating save bar. We only start watching
  // once the async socials/phone have hydrated, and skip that first settling
  // render, so loading the page doesn't count as a change.
  const [dirty, setDirty] = useState(false);
  const [hydrated, setHydrated] = useState(false);
  const skipDirtyRef = useRef(true);
  const profileSyncedRef = useRef(false);

  // Load EVERYTHING from the profile once, and only after the profile prop is
  // actually available. Previously socials/phone loaded keyed on currentUser alone,
  // so on a late/refresh load it ran with currentUserProfile still null — pulling
  // [] public socials and leaving displayName/bio blank, yet still flipping
  // `hydrated` true. Saving then wiped the real bio + public socials. Gating the
  // whole load (and `hydrated`) on currentUserProfile means the form can never
  // become saveable with stale data. Runs once.
  useEffect(() => {
    if (profileSyncedRef.current || !currentUser || !currentUserProfile) return;
    profileSyncedRef.current = true;
    skipDirtyRef.current = true;

    setDisplayName(currentUserProfile.displayName || '');
    setBio(currentUserProfile.bio || '');
    setAccent(currentUserProfile.profileAccent || 'purple');
    setAccentLevel(currentUserProfile.accentLevel || DEFAULT_ACCENT_LEVEL);
    setAccountType(currentUserProfile.accountType || 'personal');
    setPhotoPreview(prev => prev || currentUserProfile.photoURL || null);
    const b = parseMMDD(currentUserProfile.birthday);
    setBMonth(b?.month || '');
    setBDay(b?.day || '');

    Promise.all([
      loadOwnSocials(currentUser.uid, currentUserProfile.socials || []),
      loadOwnPhone(currentUser.uid, currentUserProfile),
    ]).then(([s, p]) => {
      setSocials(s);
      setPhone(p.value);
      setPhoneVisibility(p.visibility);
      setHydrated(true);
    });
  }, [currentUser, currentUserProfile]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load any existing verification request status (owner can read their own).
  useEffect(() => {
    if (!currentUser) return;
    getDoc(doc(db, 'verificationRequests', currentUser.uid))
      .then(s => { if (s.exists()) { setVerifyStatus(s.data().status || null); if (s.data().category) setEntityCategory(s.data().category); } })
      .catch(() => {});
  }, [currentUser]);

  const ENTITY_CATEGORIES = [
    ['business', 'Business'], ['public_figure', 'Public figure'], ['artist', 'Artist / band'],
    ['organization', 'Organization'], ['venue', 'Venue'], ['government', 'Government'], ['nonprofit', 'Non-profit'],
  ];

  const submitVerification = async () => {
    setVerifyBusy(true);
    try {
      await httpsCallable(functions, 'requestVerification')({
        category: entityCategory, website: verifyWebsite.trim(), notes: verifyNotes.trim(),
      });
      setVerifyStatus('pending');
      setToast({ msg: 'Verification request sent. We\'ll review it shortly.', type: 'success' });
    } catch (e) {
      console.error('verify request failed', e);
      setToast({ msg: 'Could not send request. Try again.', type: 'error' });
    }
    setVerifyBusy(false);
  };

  // Any change after hydration marks the form dirty (skip the settling render).
  useEffect(() => {
    if (!hydrated) return;
    if (skipDirtyRef.current) { skipDirtyRef.current = false; return; }
    setDirty(true);
  }, [hydrated, displayName, bio, photoFile, accent, accentLevel, accountType, phone, phoneVisibility, socials, bMonth, bDay]);

  // If a business downgrades to personal, snap any broader phone visibility back
  // into the personal-allowed range so it can't stay public by accident.
  useEffect(() => {
    if (!businessVerified && !PHONE_VIS_PERSONAL.includes(phoneVisibility)) {
      setPhoneVisibility('private');
    }
  }, [businessVerified, phoneVisibility]);

  // Warn before leaving with unsaved changes so a half hour of edits can't vanish.
  // Covers hard navigations (refresh / close tab / external) via beforeunload, and
  // in-app link clicks via a capture-phase confirm (React Router's Link bails when
  // the event is already defaultPrevented).
  useEffect(() => {
    if (!dirty) return;
    const onBeforeUnload = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', onBeforeUnload);

    const onClickCapture = (e) => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const a = e.target.closest?.('a[href]');
      if (!a || a.target === '_blank' || a.hasAttribute('download')) return;
      const href = a.getAttribute('href');
      if (!href || !href.startsWith('/') || href.startsWith('//')) return; // internal SPA links only
      if (!window.confirm('You have unsaved changes to your profile. Leave without saving?')) {
        e.preventDefault();
      }
    };
    document.addEventListener('click', onClickCapture, true);

    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
      document.removeEventListener('click', onClickCapture, true);
    };
  }, [dirty]);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 2800);
  };

  // Add a social: a $cashtag or pasted link auto-detects the platform; a bare
  // handle uses whatever's picked in the dropdown. Same smart system as the tile
  // editor (platforms.js).
  const addSocialLink = () => {
    const val = newSocialUrl.trim();
    if (!val) return;
    const key = detectPlatform(val) || newSocialPlatform;
    if (!key) { setSocialError('Pick a platform for that username.'); return; }
    const url = buildSocialUrl(key, val);
    if (!url) { setSocialError('That platform isn\'t supported yet.'); return; }
    if (socials.some(s => s.url === url)) { setSocialError('Already added.'); return; }
    setSocials(prev => [...prev, { url, visibility: 'private' }]);
    setNewSocialUrl('');
    setNewSocialPlatform('');
    setSocialError('');
  };

  const handleCopyProfileUrl = async () => {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/${currentUserProfile.username}`);
      setCopiedUrl(true);
      setTimeout(() => setCopiedUrl(false), 1500);
    } catch { /* clipboard unavailable */ }
  };

  const [cropSrc, setCropSrc] = useState(null);
  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setCropSrc(URL.createObjectURL(file));
    e.target.value = '';
  };
  const onCropSave = (blob) => {
    setPhotoFile(blob);
    setPhotoPreview(URL.createObjectURL(blob));
    setDirty(true);
    setCropSrc(null);
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    // Never write before the profile has fully loaded, or we'd persist stale-blank
    // fields over real data (the bio/socials wipe bug).
    if (!hydrated) return;
    if (!displayName.trim()) return;
    setSaving(true);
    try {
      const uid = currentUser.uid;
      let photoURL = currentUserProfile?.photoURL || null;

      if (photoFile) {
        const photoStorageRef = storageRef(storage, `profile-pictures/${uid}`);
        const blob = await downscaleImage(photoFile, 512);
        await uploadBytes(photoStorageRef, blob);
        photoURL = await getDownloadURL(photoStorageRef);
      }

      // Restricted socials go to the owner-only subcollection; only the public
      // ones are written to the world-readable user doc.
      const publicSocials = await saveSocials(uid, socials);

      // Enforce the phone rule: only a VERIFIED entity can persist a phone above
      // Top 8 (the rules reject phonePublic otherwise, so this matches them).
      const allowedPhoneVis = businessVerified ? PHONE_VIS_BUSINESS : PHONE_VIS_PERSONAL;
      const effPhoneVis = allowedPhoneVis.includes(phoneVisibility) ? phoneVisibility : 'private';
      const phonePublic = await savePhone(uid, phone, effPhoneVis);

      const updates = {
        displayName: displayName.trim(),
        bio: bio.trim(),
        photoURL,
        socials: publicSocials,
        accountType,
        profileAccent: accent,
        accentLevel,
        phonePublic: phonePublic || null,
        birthday: toMMDD(bMonth, bDay),
      };
      await updateDoc(doc(db, 'users', uid), updates);

      if (onProfileUpdate) onProfileUpdate({ ...currentUserProfile, ...updates });
      setDirty(false);
      showToast('Profile updated!');
    } catch (err) {
      console.error(err);
      showToast('Failed to save. Try again.', 'error');
    }
    setSaving(false);
  };

  const handleSignOut = async () => {
    await signOut(auth);
    // In the Android app, also drop the native session (see Nav.js). No-op on web.
    try { window.YT8Native?.signOut?.(); } catch { /* not in the app */ }
    if (onSignOut) onSignOut();
    navigate('/');
  };

  const handleDeleteAccount = async () => {
    const typed = window.prompt(
      'This permanently deletes your account, your profile, and your Top 8. ' +
      'It cannot be undone.\n\nType DELETE to confirm.'
    );
    if (typed !== 'DELETE') return;
    setSaving(true);
    try {
      // Delete the profile doc (allowed for the owner). The onUserDeleted Cloud
      // Function finishes the rest server-side: username, auth identity, photo,
      // connections, and demoting any tiles that pointed at this account.
      await deleteDoc(doc(db, 'users', currentUser.uid));
      await signOut(auth);
      try { window.YT8Native?.signOut?.(); } catch { /* not in the app */ }
      if (onSignOut) onSignOut();
      navigate('/');
    } catch (err) {
      console.error(err);
      showToast('Could not delete your account. Please try again.', 'error');
      setSaving(false);
    }
  };

  if (!currentUser || !currentUserProfile) {
    return <div style={{ padding: 40, textAlign: 'center' }}>Loading…</div>;
  }

  return (
    <>
      <Nav currentUser={currentUser} currentUserProfile={currentUserProfile} onSignOut={handleSignOut} />
      <div className="page-content" style={{ paddingTop: 32 }}>
        <div style={{ maxWidth: 520, margin: '0 auto' }}>

          <div style={{ marginBottom: 32 }}>
            <h1 style={{ fontSize: 26, fontWeight: 800, margin: 0, letterSpacing: '-0.5px' }}>Settings</h1>
            <p style={{ color: 'var(--text-muted)', marginTop: 6, fontSize: 14 }}>
              Manage your profile and account.
            </p>
          </div>

          {/* Profile section */}
          <div className="settings-section">
            <h3>Profile</h3>
            <div className="card">
              <form onSubmit={handleSaveProfile}>
                {/* Photo */}
                <div className="photo-picker" style={{ marginBottom: 20 }}>
                  <div
                    className="photo-picker-avatar"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    {photoPreview
                      ? <img src={photoPreview} alt="preview" />
                      : <span>📷</span>
                    }
                  </div>
                  <div>
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      Change Photo
                    </button>
                    <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6, marginBottom: 0 }}>
                      Appears on your profile and widget
                    </p>
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={handlePhotoChange}
                  />
                </div>

                {/* Display name */}
                <div className="form-group">
                  <label>Display Name</label>
                  <input
                    className="input"
                    value={displayName}
                    onChange={e => setDisplayName(e.target.value)}
                    required
                  />
                </div>

                {/* Bio */}
                <div className="form-group">
                  <label>Bio <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>(optional)</span></label>
                  <input
                    className="input"
                    value={bio}
                    onChange={e => setBio(e.target.value)}
                    placeholder="A short description about yourself"
                    maxLength={160}
                  />
                </div>

                {/* Profile color */}
                <div className="form-group">
                  <label>Profile color</label>
                  <span className="form-hint" style={{ marginTop: 0, marginBottom: 8, display: 'block' }}>
                    The accent on your public profile, shown to everyone who visits.
                  </span>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, opacity: accentLevel === 'none' ? 0.4 : 1 }}>
                    {ACCENTS.map(a => {
                      const sel = accent === a.key;
                      return (
                        <button
                          key={a.key}
                          type="button"
                          onClick={() => setAccent(a.key)}
                          aria-pressed={sel}
                          title={a.label}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 7,
                            fontSize: 12, padding: '6px 11px', borderRadius: 999, cursor: 'pointer',
                            border: `1px solid ${sel ? a.hex : 'var(--border)'}`,
                            background: sel ? 'var(--card)' : 'transparent',
                            color: sel ? 'var(--text)' : 'var(--muted)',
                            fontWeight: sel ? 700 : 400, fontFamily: 'var(--font)',
                          }}
                        >
                          <span style={{
                            width: 15, height: 15, borderRadius: '50%', background: a.hex, flexShrink: 0,
                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                            color: '#060610', fontSize: 10, fontWeight: 900,
                          }}>{sel ? '✓' : ''}</span>
                          {a.label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Accent level */}
                <div className="form-group">
                  <label>How much color</label>
                  <span className="form-hint" style={{ marginTop: 0, marginBottom: 8, display: 'block' }}>
                    How loudly your color shows on your profile.
                  </span>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {ACCENT_LEVELS.map(lv => {
                      const sel = accentLevel === lv.key;
                      return (
                        <button
                          key={lv.key}
                          type="button"
                          onClick={() => setAccentLevel(lv.key)}
                          aria-pressed={sel}
                          style={{
                            display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2,
                            textAlign: 'left', padding: '10px 12px', borderRadius: 10, cursor: 'pointer',
                            border: `1px solid ${sel ? 'var(--accent)' : 'var(--border)'}`,
                            background: sel ? 'var(--card)' : 'transparent',
                            fontFamily: 'var(--font)',
                          }}
                        >
                          <span style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 14, fontWeight: 700, color: 'var(--text)' }}>
                            <span style={{
                              width: 15, height: 15, borderRadius: '50%', flexShrink: 0,
                              border: `2px solid var(--accent)`,
                              background: sel ? 'var(--accent)' : 'transparent',
                              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                              color: '#060610', fontSize: 10, fontWeight: 900,
                            }}>{sel ? '✓' : ''}</span>
                            {lv.label}
                          </span>
                          <span style={{ fontSize: 12, color: 'var(--muted)', paddingLeft: 22 }}>{lv.desc}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Socials */}
                <div className="form-group">
                  <label>My Socials <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>(optional)</span></label>
                  <span className="form-hint" style={{ marginTop: 0, marginBottom: 8, display: 'block' }}>New links start private. Tap a tier on each one to choose who can see it.</span>

                  {/* Existing socials, each with a visibility tier */}
                  {socials.length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 10 }}>
                      {socials.map((s, i) => (
                        <div key={i} style={{
                          padding: '10px 12px', borderRadius: 10,
                          background: 'var(--card)', border: '1px solid var(--border)',
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <strong style={{ fontSize: 13 }}>{getPlatformLabel(s.url)}</strong>
                            <span style={{ fontSize: 12, color: 'var(--muted)', flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.url}</span>
                            <button
                              type="button"
                              onClick={() => setSocials(prev => prev.filter((_, j) => j !== i))}
                              style={{ background: 'none', border: 'none', color: 'var(--muted)', cursor: 'pointer', padding: 0, fontSize: 17, lineHeight: 1 }}
                              title="Remove"
                            >×</button>
                          </div>
                          <div style={{ display: 'flex', gap: 4, marginTop: 8 }}>
                            {VIS.map(v => (
                              <button
                                key={v.key}
                                type="button"
                                title={v.desc}
                                onClick={() => setSocials(prev => prev.map((x, j) => j === i ? { ...x, visibility: v.key } : x))}
                                style={{
                                  fontSize: 11, padding: '4px 9px', borderRadius: 8, cursor: 'pointer',
                                  border: `1px solid ${s.visibility === v.key ? 'var(--accent)' : 'var(--border)'}`,
                                  background: s.visibility === v.key ? 'var(--accent)' : 'transparent',
                                  color: s.visibility === v.key ? '#060610' : 'var(--muted)',
                                  fontWeight: s.visibility === v.key ? 700 : 400,
                                  fontFamily: 'var(--font)',
                                }}
                              >{v.label}</button>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Add new — platform auto-detects from a link or $cashtag, or pick one */}
                  <select
                    className="input"
                    value={newSocialPlatform}
                    onChange={e => setNewSocialPlatform(e.target.value)}
                    style={{ marginBottom: 8 }}
                  >
                    <option value="">Platform</option>
                    {SOCIAL_PLATFORMS.map(p => (
                      <option key={p.key} value={p.key}>{p.name}</option>
                    ))}
                  </select>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <input
                      className="input"
                      value={newSocialUrl}
                      onChange={e => {
                        const v = e.target.value;
                        setNewSocialUrl(v); setSocialError('');
                        const d = detectPlatform(v); if (d) setNewSocialPlatform(d);
                      }}
                      onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addSocialLink(); } }}
                      placeholder="Username, $cashtag, or full link"
                      style={{ flex: 1 }}
                    />
                    <button type="button" className="btn btn-sm" onClick={addSocialLink} style={{ flexShrink: 0 }}>Add</button>
                  </div>
                  {socialError && <div className="form-error">{socialError}</div>}
                  <div className="form-hint">
                    Paste a link, enter a $cashtag, or pick a platform and type a username.{' '}
                    <button
                      type="button"
                      onClick={() => setShowSupported(v => !v)}
                      style={{ background: 'none', border: 'none', color: 'var(--accent)', cursor: 'pointer', padding: 0, fontSize: 'inherit', fontFamily: 'var(--font)', textDecoration: 'underline' }}
                    >
                      {showSupported ? 'Hide' : 'See supported sites'}
                    </button>
                  </div>
                  {showSupported && (
                    <div style={{
                      marginTop: 8, padding: '10px 12px', borderRadius: 10,
                      background: 'var(--card)', border: '1px solid var(--border)',
                      fontSize: 12, color: 'var(--muted)', lineHeight: 1.7,
                    }}>
                      {SUPPORTED_PLATFORM_NAMES.join(' · ')}
                      <div style={{ marginTop: 6 }}>
                        Missing one? Email hello@yourtop8.com and we will add it.
                      </div>
                    </div>
                  )}
                </div>

                {/* Birthday (optional) */}
                <div className="form-group">
                  <label>Birthday <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>(optional)</span></label>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <select
                      className="input"
                      value={bMonth}
                      onChange={e => { const m = Number(e.target.value); setBMonth(m); if (m && bDay > daysInMonth(m - 1)) setBDay(daysInMonth(m - 1)); }}
                      style={{ flex: 2 }}
                    >
                      <option value="">Month</option>
                      {MONTHS.map((name, i) => <option key={i} value={i + 1}>{name}</option>)}
                    </select>
                    <select
                      className="input"
                      value={bDay}
                      onChange={e => setBDay(Number(e.target.value))}
                      disabled={!bMonth}
                      style={{ flex: 1 }}
                    >
                      <option value="">Day</option>
                      {Array.from({ length: bMonth ? daysInMonth(bMonth - 1) : 31 }, (_, i) => i + 1)
                        .map(d => <option key={d} value={d}>{d}</option>)}
                    </select>
                    {(bMonth || bDay) && (
                      <button
                        type="button"
                        onClick={() => { setBMonth(''); setBDay(''); }}
                        style={{ background: 'none', border: 'none', color: 'var(--muted)', cursor: 'pointer', fontSize: 13, fontFamily: 'var(--font)', textDecoration: 'underline' }}
                      >Clear</button>
                    )}
                  </div>
                  <span className="form-hint">
                    Only the month and day are shown, never the year. On your birthday your tile gets a 🎂 frame everywhere it appears, including home-screen widgets.
                  </span>
                </div>

                {/* Account type */}
                <div className="form-group">
                  <label>Account type</label>
                  <div style={{ display: 'flex', gap: 8 }}>
                    {[{ key: 'personal', label: 'Personal' }, { key: 'business', label: 'Entity' }].map(t => (
                      <button
                        key={t.key}
                        type="button"
                        onClick={() => {
                          if (t.key === 'business' && accountType !== 'business') {
                            const ok = window.confirm('Entity accounts are for businesses, public figures, organizations, and the like. You can request verification to get a badge and share a public phone number. Switch to an entity account?');
                            if (!ok) return;
                          }
                          setAccountType(t.key);
                        }}
                        style={{
                          fontSize: 13, padding: '8px 16px', borderRadius: 10, cursor: 'pointer',
                          border: `1px solid ${accountType === t.key ? 'var(--accent)' : 'var(--border)'}`,
                          background: accountType === t.key ? 'var(--accent)' : 'transparent',
                          color: accountType === t.key ? '#060610' : 'var(--muted)',
                          fontWeight: accountType === t.key ? 700 : 400,
                          fontFamily: 'var(--font)',
                        }}
                      >{t.label}</button>
                    ))}
                  </div>
                  <span className="form-hint">
                    Anyone can switch to an entity account. The verified badge and a public phone number require verification (below).
                  </span>
                </div>

                {/* Entity category + verification */}
                {isBusiness && (
                  <div className="form-group">
                    <label>Category</label>
                    <select
                      className="input"
                      value={entityCategory}
                      onChange={e => setEntityCategory(e.target.value)}
                      disabled={businessVerified}
                    >
                      {ENTITY_CATEGORIES.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
                    </select>

                    <div style={{
                      marginTop: 12, padding: 14, borderRadius: 10,
                      border: '1px solid var(--border)', background: 'var(--card-2)',
                    }}>
                      {businessVerified ? (
                        <div style={{ fontSize: 13, color: 'var(--text)', fontWeight: 600 }}>
                          ✓ Verified. Your badge is live and you can share a public phone number.
                        </div>
                      ) : verifyStatus === 'pending' ? (
                        <div style={{ fontSize: 13, color: 'var(--muted)' }}>
                          Verification request received. We review these manually, so it may take a little time. We'll notify you.
                        </div>
                      ) : (
                        <>
                          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 6 }}>Request verification</div>
                          <p style={{ fontSize: 12, color: 'var(--muted)', margin: '0 0 10px', lineHeight: 1.5 }}>
                            Verification confirms you really are who you say you are. It unlocks the badge and a public phone number. Add a website or social we can check, plus anything that helps us confirm you. We may reach out for proof.
                          </p>
                          <input
                            className="input"
                            value={verifyWebsite}
                            onChange={e => setVerifyWebsite(e.target.value)}
                            placeholder="Website or official social URL"
                            style={{ marginBottom: 8 }}
                          />
                          <textarea
                            className="input"
                            value={verifyNotes}
                            onChange={e => setVerifyNotes(e.target.value)}
                            placeholder="Anything that helps us verify you (optional)"
                            rows={2}
                            style={{ marginBottom: 10, resize: 'vertical' }}
                          />
                          <button
                            type="button"
                            className="btn btn-primary btn-sm"
                            onClick={submitVerification}
                            disabled={verifyBusy}
                          >
                            {verifyBusy ? 'Sending…' : 'Request verification'}
                          </button>
                          {verifyStatus === 'rejected' && (
                            <span style={{ marginLeft: 10, fontSize: 12, color: 'var(--muted)' }}>
                              A previous request wasn't approved. You can submit again.
                            </span>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                )}

                {/* Phone number */}
                <div className="form-group">
                  <label>Phone number <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>(optional)</span></label>
                  <input
                    className="input"
                    type="tel"
                    value={phone}
                    onChange={e => setPhone(e.target.value)}
                    placeholder="(555) 123-4567"
                  />
                  {phone.trim() && (
                    <div style={{ display: 'flex', gap: 4, marginTop: 8, flexWrap: 'wrap' }}>
                      {(businessVerified ? PHONE_VIS_BUSINESS : PHONE_VIS_PERSONAL).map(key => {
                        const v = VIS.find(x => x.key === key);
                        const sel = phoneVisibility === key;
                        return (
                          <button
                            key={key}
                            type="button"
                            title={v.desc}
                            onClick={() => setPhoneVisibility(key)}
                            style={{
                              fontSize: 11, padding: '4px 9px', borderRadius: 8, cursor: 'pointer',
                              border: `1px solid ${sel ? 'var(--accent)' : 'var(--border)'}`,
                              background: sel ? 'var(--accent)' : 'transparent',
                              color: sel ? '#060610' : 'var(--muted)',
                              fontWeight: sel ? 700 : 400,
                              fontFamily: 'var(--font)',
                            }}
                          >{v.label}</button>
                        );
                      })}
                    </div>
                  )}
                  <span className="form-hint">
                    {businessVerified
                      ? 'Choose who can see your number. It is private until you change that.'
                      : 'You can share a number with your Top 8, or keep it private. A public or connections-wide number requires a verified entity account (so nobody can post a number they do not own).'}
                  </span>
                </div>

                {/* Username (read-only) */}
                <div className="form-group">
                  <label>Username</label>
                  <input
                    className="input"
                    value={`@${currentUserProfile.username}`}
                    disabled
                    style={{ opacity: 0.5 }}
                  />
                  <span className="form-hint">Username cannot be changed</span>
                </div>

                {/* Email (read-only) */}
                <div className="form-group">
                  <label>Email</label>
                  <input
                    className="input"
                    value={currentUser.email || ''}
                    disabled
                    style={{ opacity: 0.5 }}
                  />
                </div>

                {/* Save lives in the floating bar (appears only when there are
                    unsaved changes). A hidden submit keeps Enter-to-save working. */}
                <button type="submit" style={{ display: 'none' }} aria-hidden="true" tabIndex={-1} />
              </form>
            </div>
          </div>

          {/* Links section */}
          <div className="settings-section">
            <h3>Your Profile</h3>
            <div className="card">
              <div className="settings-row">
                <div className="settings-row-label">
                  <strong>Your profile link</strong>
                  <span style={{ wordBreak: 'break-all' }}>yourtop8.com/{currentUserProfile.username}</span>
                </div>
                <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                  <button type="button" className="btn btn-secondary btn-sm" onClick={handleCopyProfileUrl}>
                    {copiedUrl ? 'Copied!' : 'Copy'}
                  </button>
                  <a
                    href={`/${currentUserProfile.username}`}
                    target="_blank"
                    rel="noreferrer"
                    className="btn btn-secondary btn-sm"
                  >
                    View →
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* App section */}
          <div className="settings-section">
            <h3>Mobile App</h3>
            <div className="card">
              {typeof window !== 'undefined' && window.YT8Native?.requestReview && (
                <div className="settings-row">
                  <div className="settings-row-label">
                    <strong>Rate the app</strong>
                    <span>Enjoying Your Top 8? Leave a review.</span>
                  </div>
                  <button
                    className="btn btn-ghost btn-sm"
                    onClick={() => { try { window.YT8Native.requestReview(); } catch { /* not in the app */ } }}
                  >
                    Rate
                  </button>
                </div>
              )}
              <div className="settings-row">
                <div className="settings-row-label">
                  <strong>Android App</strong>
                  <span>Get the live home screen widget</span>
                </div>
                <span className="badge badge-primary" style={{ flexShrink: 0 }}>Coming Soon</span>
              </div>
              <div className="settings-row">
                <div className="settings-row-label">
                  <strong>iOS App</strong>
                  <span>Widget for your iPhone home screen</span>
                </div>
                <span className="badge badge-primary" style={{ flexShrink: 0 }}>Coming Soon</span>
              </div>
            </div>
          </div>

          {/* Account section */}
          <div className="settings-section">
            <h3>Account</h3>
            <div className="card">
              <div className="settings-row">
                <div className="settings-row-label">
                  <strong>Sign Out</strong>
                  <span>Sign out of your account on this device</span>
                </div>
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={handleSignOut}
                  style={{ flexShrink: 0 }}
                >
                  Sign Out
                </button>
              </div>
            </div>
          </div>

          {/* Danger zone */}
          <div className="settings-section">
            <h3>Danger Zone</h3>
            <div className="card">
              <div className="settings-row">
                <div className="settings-row-label">
                  <strong>Delete Account</strong>
                  <span>Permanently delete your account, profile, and Top 8. This cannot be undone.</span>
                </div>
                <button
                  className="btn btn-sm"
                  onClick={handleDeleteAccount}
                  disabled={saving}
                  style={{ flexShrink: 0, background: 'rgba(255,80,80,0.15)', border: '1px solid rgba(255,80,80,0.3)', color: '#ff6b6b' }}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* Floating save bar: only appears once something's changed. */}
      {dirty && (
        <div style={{
          position: 'fixed', left: '50%', bottom: 'calc(env(safe-area-inset-bottom, 0px) + 18px)',
          transform: 'translateX(-50%)', zIndex: 60, width: 'min(520px, calc(100% - 32px))',
        }}>
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
            padding: '10px 12px 10px 16px', borderRadius: 14,
            background: 'var(--card)', border: '1px solid var(--accent)',
            boxShadow: '0 10px 30px rgba(0,0,0,0.45)',
          }}>
            <span style={{ fontSize: 14, fontWeight: 600 }}>Unsaved changes</span>
            <button
              className="btn btn-primary btn-sm"
              onClick={handleSaveProfile}
              disabled={saving || !displayName.trim()}
            >
              {saving ? <span className="spinner" style={{ width: 14, height: 14, borderTopColor: '#0d0d0d' }} /> : 'Save'}
            </button>
          </div>
        </div>
      )}

      {cropSrc && (
        <AvatarCropper src={cropSrc} onCancel={() => setCropSrc(null)} onSave={onCropSave} />
      )}
      {toast && <Toast message={toast.msg} type={toast.type} />}
    </>
  );
}

export default Settings;
